<template>
  <div class="feedback-page">
    <main class="feedback-content">
      <section class="feedback-hero">
        <h1>{{ titleText }}</h1>
      </section>

      <section class="notice-card">
        <h2>重要提示</h2>
        <p>轻拍患儿背部，安抚患儿情绪，观察患儿操作后反应（精神、食欲、哭闹情况）是否都正常。</p>
      </section>

      <section class="feedback-card">
        <h2>异常记录</h2>
        <p class="field-title">宝宝是否出现以下异常情况</p>
        <van-checkbox-group v-model="abnormalTypes" checked-color="#2F8F6B">
          <van-cell-group>
            <van-cell
              v-for="item in abnormalOptions"
              :key="item.value"
              :title="item.label"
              clickable
              @click="toggleAbnormal(item.value)"
            >
              <template #right-icon>
                <van-checkbox :name="item.value" shape="square" @click.stop />
              </template>
            </van-cell>
          </van-cell-group>
        </van-checkbox-group>

        <van-cell title="宝宝正常" clickable class="normal-cell" @click="toggleNormal">
          <template #right-icon>
            <van-checkbox v-model="isNormal" checked-color="#2F8F6B" shape="square" @click.stop="onNormalCheckboxClick" />
          </template>
        </van-cell>

        <van-field
          v-if="showOtherInput"
          v-model="otherDesc"
          type="textarea"
          rows="3"
          autosize
          maxlength="300"
          show-word-limit
          label="其他"
          placeholder="请描述出现的问题并及时咨询照护师"
        />
      </section>
    </main>

    <div class="bottom-action">
      <van-button block color="#2F8F6B" :disabled="!canSubmit" :loading="submitting" @click="submitFeedback">
        {{ isNormal ? '提交反馈' : '提交并咨询照护师' }}
      </van-button>
    </div>
  </div>
</template>

<script>
import { resolveTcmStudyToken, submitMassageFeedback } from '@/api/tcmStudy';

export default {
    name: 'TcmMassageFeedback',
    data () {
        return {
            token: '',
            isNormal: false,
            abnormalTypes: [],
            otherDesc: '',
            abnormalOptions: [
                { label: '皮肤发红', value: 'skin_redness' },
                { label: '腹胀', value: 'abdominal_distension' },
                { label: '呕吐', value: 'vomiting' },
                { label: '出现剧烈哭闹', value: 'severe_crying' },
                { label: '面色发绀', value: 'cyanosis' },
                { label: '烦躁不安', value: 'restlessness' },
                { label: '其他', value: 'other' }
            ],
            submitting: false
        };
    },
    computed: {
        massagePlanTitle () {
            return this.$route.query.massage_plan_title || '小儿推拿方案1';
        },
        titleText () {
            const count = this.$route.query.completed_count;
            return count ? `恭喜宝妈已为宝宝完成第${count}次${this.massagePlanTitle}` : `恭喜宝妈已完成本次${this.massagePlanTitle}`;
        },
        showOtherInput () {
            return this.abnormalTypes.indexOf('other') !== -1;
        },
        canSubmit () {
            if (this.isNormal) {
                return true;
            }
            if (!this.abnormalTypes.length) {
                return false;
            }
            if (this.showOtherInput && this.otherDesc.trim().length < 5) {
                return false;
            }
            return true;
        }
    },
    mounted () {
        this.token = resolveTcmStudyToken(this.$route);
    },
    watch: {
        abnormalTypes (value) {
            if (value.length) {
                this.isNormal = false;
            }
            if (value.indexOf('other') === -1) {
                this.otherDesc = '';
            }
        }
    },
    methods: {
        returnToTaskPage () {
            const query = {
                ...this.$route.query,
                date: this.$route.query.record_date || this.$route.query.date
            };
            delete query.return_path;
            this.$router.replace({
                path: this.$route.query.return_path || '/tcm-study/home',
                query
            });
        },
        payload () {
            return {
                baby_id: this.$route.query.baby_id,
                subject_id: this.$route.query.subject_id || undefined,
                record_date: this.$route.query.record_date,
                task_key: this.$route.query.task_key || 'massage'
            };
        },
        toggleAbnormal (value) {
            const index = this.abnormalTypes.indexOf(value);
            if (index === -1) {
                this.abnormalTypes.push(value);
            } else {
                this.abnormalTypes.splice(index, 1);
            }
            if (this.abnormalTypes.length) {
                this.isNormal = false;
            }
        },
        toggleNormal () {
            this.isNormal = !this.isNormal;
            if (this.isNormal) {
                this.abnormalTypes = [];
                this.otherDesc = '';
            }
        },
        onNormalCheckboxClick () {
            if (this.isNormal) {
                this.abnormalTypes = [];
                this.otherDesc = '';
            }
        },
        discomfortDesc () {
            if (this.isNormal) {
                return '';
            }
            const labels = this.abnormalOptions
                .filter(item => this.abnormalTypes.indexOf(item.value) !== -1 && item.value !== 'other')
                .map(item => item.label);
            if (this.showOtherInput && this.otherDesc.trim()) {
                labels.push(`其他：${this.otherDesc.trim()}`);
            }
            return labels.join('；');
        },
        async submitFeedback () {
            if (!this.canSubmit || this.submitting) {
                return;
            }
            this.submitting = true;
            try {
                const result = await submitMassageFeedback(this.token, {
                    ...this.payload(),
                    is_normal: this.isNormal ? 1 : 0,
                    abnormal_types: this.abnormalTypes,
                    abnormal_other_desc: this.showOtherInput ? this.otherDesc.trim() : '',
                    discomfort_desc: this.discomfortDesc()
                });
                if (!this.isNormal) {
                    this.openIm(result && result.im && result.im.message);
                    return;
                }
                this.$toast.success('反馈已提交');
                this.returnToTaskPage();
            } catch (error) {
                this.$toast(error.message || '提交失败');
            } finally {
                this.submitting = false;
            }
        },
        openIm (message) {
            const payload = {
                source: 'tcm-massage',
                message: message || this.discomfortDesc()
            };
            if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.openIM) {
                window.webkit.messageHandlers.openIM.postMessage(payload);
                return;
            }
            if (window.healthApp && typeof window.healthApp.openIM === 'function') {
                window.healthApp.openIM(payload);
                return;
            }
            this.$toast('已提交，返回 App 咨询照护师');
            this.returnToTaskPage();
        }
    }
};
</script>

<style lang="sass" scoped>
.feedback-page
    min-height: 100vh
    background: #f7f8fa
    color: #1f2329
    padding-top: env(safe-area-inset-top)
    box-sizing: border-box

.feedback-content
    padding: 24px 16px 104px

.feedback-hero h1
    margin: 0 0 16px
    font-size: 20px
    line-height: 28px
    font-weight: 700

.notice-card,
.feedback-card
    margin-bottom: 12px
    padding: 16px
    background: #ffffff
    border-radius: 8px
    box-sizing: border-box

.notice-card
    background: #fff7e6

.notice-card h2,
.feedback-card h2
    margin: 0 0 8px
    font-size: 16px
    line-height: 24px
    font-weight: 600

.notice-card p
    margin: 0
    color: #8a5a00
    font-size: 14px
    line-height: 22px

.field-title
    margin: 0 0 10px
    color: #4e5969
    font-size: 14px
    line-height: 20px

.normal-cell
    margin-top: 8px
    border-top: 1px solid #f0f2f5

.feedback-card ::v-deep .van-cell__title
    color: #1f2329
    font-size: 14px

.feedback-card ::v-deep .van-checkbox__icon
    font-size: 18px

::v-deep .van-cell
    padding-left: 0
    padding-right: 0

::v-deep .van-field
    margin-top: 10px
    padding-left: 0
    padding-right: 0

.bottom-action
    position: fixed
    left: 0
    right: 0
    bottom: 0
    padding: 12px 16px calc(12px + env(safe-area-inset-bottom))
    background: #ffffff
    box-shadow: 0 -2px 8px rgba(0, 0, 0, .04)
    box-sizing: border-box
</style>
